/**
 * ZIP codes and prefixes as removable chips. Type one (or paste several,
 * separated by commas or spaces) and press Enter or Add; each chip's button
 * removes it. Duplicates are dropped; anything that is not 3–5 digits stays
 * in the box with a message instead of becoming a chip.
 */
import { useId, useState } from 'react';
import { Field } from '../ui/kit';
import { ZIP_PREFIX_RE } from './model';

export function ZipChips({
  label,
  hint,
  values,
  onChange,
  max,
  disabled,
}: {
  label: string;
  hint: string;
  values: string[];
  onChange: (values: string[]) => void;
  max: number;
  disabled?: boolean;
}) {
  const [entry, setEntry] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const listId = useId();
  const full = values.length >= max;

  const add = () => {
    const parts = entry
      .split(/[\s,;]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const bad = parts.filter((p) => !ZIP_PREFIX_RE.test(p));
    const good = parts.filter((p) => ZIP_PREFIX_RE.test(p) && !values.includes(p));
    const room = Math.max(0, max - values.length);
    const taken = [...new Set(good)].slice(0, room);
    if (taken.length) onChange([...values, ...taken]);
    if (bad.length) {
      setEntry(bad.join(', '));
      setProblem(`Use 3 to 5 digits, e.g. 98103 or 981. Not added: ${bad.join(', ')}.`);
    } else if (good.length > taken.length) {
      setEntry('');
      setProblem(`You can list up to ${max}. Remove some to add more.`);
    } else {
      setEntry('');
      setProblem(null);
    }
  };

  return (
    <div className="stack-sm">
      <Field label={label} optional hint={hint} issues={problem ? [{ path: 'zip', message: problem }] : []}>
        {(p) => (
          <div className="row" style={{ flexWrap: 'wrap', alignItems: 'stretch' }}>
            <input
              {...p}
              className="input mono"
              style={{ flex: '1 1 160px', minWidth: 0 }}
              inputMode="numeric"
              autoComplete="off"
              value={entry}
              disabled={disabled || full}
              placeholder={full ? `${max} listed` : '98103, 981'}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <button type="button" className="btn" disabled={disabled || full || !entry.trim()} onClick={add}>
              Add
            </button>
          </div>
        )}
      </Field>
      {values.length ? (
        <ul id={listId} className="chips" aria-label={`Listed (${values.length} of ${max})`} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {values.map((v) => (
            <li key={v}>
              <button
                type="button"
                className="chip mono"
                disabled={disabled}
                aria-label={`Remove ${v}`}
                onClick={() => onChange(values.filter((x) => x !== v))}
              >
                {v} <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          None listed.
        </p>
      )}
    </div>
  );
}
